import type { SkillProgressDto } from '@dbrain/shared';

/**
 * Phase 16c.6 (D-433): "Skill ekle" while it waits for a source. The dialog picks a progress id for its look (POST
 * /api/skills/source, /preview, /:skillId/update-from-source with it) and asks GET /api/skills/progress/:progressId
 * while the look runs. The server says `retrying` from the moment a request whose site did not answer (10 s for the
 * TLS through egress) is tried once more until that try ends; the dialog then says "GitHub cevap vermedi, tekrar
 * deniyorum…". Nothing is guessed from a clock: before the first answer and after the look nothing is shown.
 * DOM-free, shared by the web and the iPhone app.
 */

/** the first question after this long (a look usually ends before it), then one a second */
export const PROGRESS_FIRST_MS = 2_000;
export const PROGRESS_EVERY_MS = 1_000;

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** the platform's random bytes, or Math.random where there are none (the iPhone app's engine): the id is no secret */
const anyRandom = (bytes: Uint8Array): Uint8Array => {
  const crypto = (globalThis as { crypto?: { getRandomValues?: (b: Uint8Array) => Uint8Array } }).crypto;
  if (crypto?.getRandomValues) return crypto.getRandomValues(bytes);
  for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return bytes;
};

/**
 * 24 random characters the server accepts (A–Z, a–z, 0–9, - and _). Not a secret: the server keeps a look's progress
 * under the person's own ids, so nobody else can read it by guessing.
 */
export function newProgressId(random: (bytes: Uint8Array) => Uint8Array = anyRandom): string {
  const bytes = random(new Uint8Array(24));
  let out = '';
  for (const byte of bytes) out += BASE64URL[byte & 63];
  return out;
}

/**
 * Asks while a look runs: first after `firstMs`, then every `everyMs` after the last answer (never two at once). A
 * question that fails is skipped (the look's own answer says what went wrong). Returns the function that stops it.
 */
export function watchProgress(
  ask: () => Promise<SkillProgressDto>,
  onChange: (progress: SkillProgressDto) => void,
  timing: { firstMs?: number; everyMs?: number } = {},
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const next = (ms: number) => {
    timer = setTimeout(() => void once(), ms);
  };
  const once = async () => {
    try {
      const progress = await ask();
      if (!stopped) onChange(progress);
    } catch {
      // skipped: the next question, or the look's own answer
    }
    if (!stopped) next(timing.everyMs ?? PROGRESS_EVERY_MS);
  };
  next(timing.firstMs ?? PROGRESS_FIRST_MS);
  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
  };
}

/** what the dialog says while waiting: nothing, or that GitHub (or the link's site) is being asked again */
export type RetryNotice = { kind: 'github' } | { kind: 'link'; host: string };

export function retryNotice(progress: SkillProgressDto | null | undefined): RetryNotice | null {
  if (progress?.state !== 'retrying') return null;
  if (progress.kind === 'link' && progress.host) return { kind: 'link', host: progress.host };
  return { kind: 'github' };
}
